import { Controller } from "@hotwired/stimulus"

/**
 * Video Compare Controller
 *
 * Two modes:
 * 1. Synced dual players (side-by-side): two <video> elements with shared
 *    play/pause, scrubber, frame step, speed control, and rAF sync loop.
 * 2. Video slider (paused frames): both videos paused at same timestamp,
 *    current frames drawn to Canvas via drawImage(), slider divider on canvases.
 */
export default class extends Controller {
  static targets = [
    "videoA",
    "videoB",
    "scrubber",
    "timeDisplay",
    "speedDisplay",
    "playBtn",
    "canvasA",
    "canvasB",
    "slider",
    "sliderViewport",
  ]
  static values = {
    fps: { type: Number, default: 30 },
    paused: { type: Boolean, default: true },
    sliderMode: { type: Boolean, default: false },
  }

  declare videoATarget: HTMLVideoElement
  declare videoBTarget: HTMLVideoElement
  declare hasScrubberTarget: boolean
  declare scrubberTarget: HTMLInputElement
  declare hasTimeDisplayTarget: boolean
  declare timeDisplayTarget: HTMLElement
  declare hasSpeedDisplayTarget: boolean
  declare speedDisplayTarget: HTMLElement
  declare hasPlayBtnTarget: boolean
  declare playBtnTarget: HTMLElement
  declare hasCanvasATarget: boolean
  declare canvasATarget: HTMLCanvasElement
  declare hasCanvasBTarget: boolean
  declare canvasBTarget: HTMLCanvasElement
  declare hasSliderTarget: boolean
  declare sliderTarget: HTMLElement
  declare hasSliderViewportTarget: boolean
  declare sliderViewportTarget: HTMLElement
  declare fpsValue: number
  declare pausedValue: boolean
  declare sliderModeValue: boolean

  private rafId: number | null = null
  private speed = 1
  private sliderPosition = 50
  private isDragging = false

  // Bound references for cleanup
  private boundPointerUp: (e: PointerEvent) => void = () => {}
  private boundPointerMove: (e: PointerEvent) => void = () => {}

  connect() {
    this.boundPointerUp = this.handlePointerUp.bind(this)
    this.boundPointerMove = this.handlePointerMove.bind(this)
    document.addEventListener("pointerup", this.boundPointerUp)
    document.addEventListener("pointermove", this.boundPointerMove)

    this.videoATarget.addEventListener("loadedmetadata", () => {
      this.initScrubber()
      if (this.sliderModeValue) {
        this.drawFrames()
      }
    })

    // In slider mode, also draw when video B is ready
    if (this.sliderModeValue) {
      this.videoBTarget.addEventListener("loadedmetadata", () => {
        if (this.videoATarget.readyState >= 1) {
          this.drawFrames()
        }
      })
    }
  }

  disconnect() {
    if (this.rafId) cancelAnimationFrame(this.rafId)
    document.removeEventListener("pointerup", this.boundPointerUp)
    document.removeEventListener("pointermove", this.boundPointerMove)
  }

  // ── Scrubber init ──────────────────────────────────────────

  private initScrubber() {
    if (!this.hasScrubberTarget) return
    this.scrubberTarget.max = String(this.videoATarget.duration)
    this.scrubberTarget.step = String(1 / this.fpsValue)
    this.updateTimeDisplay()
  }

  // ── Playback Controls ─────────────────────────────────────

  togglePlay() {
    if (this.sliderModeValue) return // No playback in slider mode

    if (this.videoATarget.paused) {
      this.videoATarget.play()
      this.videoBTarget.play()
      this.pausedValue = false
      this.startSync()
    } else {
      this.videoATarget.pause()
      this.videoBTarget.pause()
      this.pausedValue = true
      this.stopSync()
    }
    this.updatePlayButton()
  }

  seek() {
    if (!this.hasScrubberTarget) return
    const time = parseFloat(this.scrubberTarget.value)
    this.videoATarget.currentTime = time
    this.videoBTarget.currentTime = time
    this.updateTimeDisplay()
    if (this.sliderModeValue) {
      // Wait for seek to complete, then draw frames
      this.videoATarget.addEventListener(
        "seeked",
        () => this.drawFrames(),
        { once: true },
      )
    }
  }

  stepForward() {
    const step = 1 / this.fpsValue
    this.videoATarget.currentTime = Math.min(
      this.videoATarget.duration,
      this.videoATarget.currentTime + step,
    )
    this.videoBTarget.currentTime = this.videoATarget.currentTime
    this.updateTimeDisplay()
    this.updateScrubberPosition()
    if (this.sliderModeValue) {
      this.videoATarget.addEventListener(
        "seeked",
        () => this.drawFrames(),
        { once: true },
      )
    }
  }

  stepBackward() {
    const step = 1 / this.fpsValue
    this.videoATarget.currentTime = Math.max(
      0,
      this.videoATarget.currentTime - step,
    )
    this.videoBTarget.currentTime = this.videoATarget.currentTime
    this.updateTimeDisplay()
    this.updateScrubberPosition()
    if (this.sliderModeValue) {
      this.videoATarget.addEventListener(
        "seeked",
        () => this.drawFrames(),
        { once: true },
      )
    }
  }

  setSpeed(event: Event) {
    const btn = event.currentTarget as HTMLButtonElement
    this.speed = parseFloat(btn.dataset.speed || "1")
    this.videoATarget.playbackRate = this.speed
    this.videoBTarget.playbackRate = this.speed
    if (this.hasSpeedDisplayTarget) {
      this.speedDisplayTarget.textContent = `${this.speed}x`
    }
    // Highlight active speed button
    this.element
      .querySelectorAll("[data-speed]")
      .forEach((el) => {
        el.classList.toggle(
          "bg-neutral-content/20",
          (el as HTMLElement).dataset.speed === String(this.speed),
        )
      })
  }

  // ── Sync Loop ─────────────────────────────────────────────

  private startSync() {
    this.rafId = requestAnimationFrame(this.syncLoop)
  }

  private stopSync() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
  }

  private syncLoop = () => {
    // Sync secondary to primary — correct drift > 1 frame at 30fps
    const drift = Math.abs(
      this.videoATarget.currentTime - this.videoBTarget.currentTime,
    )
    if (drift > 0.033) {
      this.videoBTarget.currentTime = this.videoATarget.currentTime
    }

    this.updateScrubberPosition()
    this.updateTimeDisplay()

    if (!this.pausedValue) {
      this.rafId = requestAnimationFrame(this.syncLoop)
    }
  }

  // ── Video Slider (paused frames) ──────────────────────────

  drawFrames() {
    if (!this.hasCanvasATarget || !this.hasCanvasBTarget) return

    const videoA = this.videoATarget
    const videoB = this.videoBTarget

    if (videoA.readyState < 2 || videoB.readyState < 2) {
      // Not enough data — retry when data is available
      videoA.addEventListener("canplay", () => this.drawFrames(), {
        once: true,
      })
      return
    }

    const canvasA = this.canvasATarget
    const canvasB = this.canvasBTarget

    // Size canvases to video dimensions
    canvasA.width = videoA.videoWidth
    canvasA.height = videoA.videoHeight
    canvasB.width = videoB.videoWidth
    canvasB.height = videoB.videoHeight

    const ctxA = canvasA.getContext("2d")!
    const ctxB = canvasB.getContext("2d")!

    ctxA.drawImage(videoA, 0, 0)
    ctxB.drawImage(videoB, 0, 0)

    // Apply slider clip
    this.applySliderClip()
  }

  startSliderDrag(event: PointerEvent) {
    this.isDragging = true
    event.preventDefault()
    ;(event.target as HTMLElement)?.setPointerCapture?.(event.pointerId)
    this.updateSliderPosition(event)
  }

  sliderViewportClick(event: PointerEvent) {
    if (this.isDragging) return
    this.isDragging = true
    event.preventDefault()
    this.updateSliderPosition(event)
  }

  sliderKeyboard(event: KeyboardEvent) {
    if (event.key === "ArrowLeft") {
      this.sliderPosition = Math.max(0, this.sliderPosition - 1)
    } else if (event.key === "ArrowRight") {
      this.sliderPosition = Math.min(100, this.sliderPosition + 1)
    } else {
      return
    }
    event.preventDefault()
    this.applySliderClip()
    if (this.hasSliderTarget) {
      this.sliderTarget.style.left = `${this.sliderPosition}%`
    }
  }

  private handlePointerMove(event: PointerEvent) {
    if (this.isDragging) {
      this.updateSliderPosition(event)
    }
  }

  private handlePointerUp() {
    this.isDragging = false
  }

  private updateSliderPosition(event: PointerEvent) {
    if (!this.hasSliderViewportTarget) return
    const rect = this.sliderViewportTarget.getBoundingClientRect()
    const x = event.clientX - rect.left
    this.sliderPosition = Math.max(0, Math.min(100, (x / rect.width) * 100))
    if (this.hasSliderTarget) {
      this.sliderTarget.style.left = `${this.sliderPosition}%`
    }
    this.applySliderClip()
  }

  private applySliderClip() {
    if (this.hasCanvasBTarget) {
      this.canvasBTarget.style.clipPath = `inset(0 0 0 ${this.sliderPosition}%)`
    }
  }

  // ── Helpers ───────────────────────────────────────────────

  private updatePlayButton() {
    if (!this.hasPlayBtnTarget) return
    this.playBtnTarget.textContent = this.pausedValue ? "\u25B6" : "\u23F8"
  }

  private updateScrubberPosition() {
    if (!this.hasScrubberTarget) return
    this.scrubberTarget.value = String(this.videoATarget.currentTime)
  }

  private updateTimeDisplay() {
    if (!this.hasTimeDisplayTarget) return
    const t = this.videoATarget.currentTime
    const d = this.videoATarget.duration || 0
    this.timeDisplayTarget.textContent = `${this.formatTime(t)} / ${this.formatTime(d)}`
  }

  private formatTime(s: number): string {
    if (!isFinite(s)) return "0:00.00"
    const m = Math.floor(s / 60)
    const sec = Math.floor(s % 60)
    const ms = Math.floor((s % 1) * 100)
    return `${m}:${String(sec).padStart(2, "0")}.${String(ms).padStart(2, "0")}`
  }
}
